import { DanceTeam, DanceTeamUser, User } from "@vote4dance/judging-schema";
import { StreamSource } from "../hooks/streams";
import { DanceTeamId, FederationId, OrganizationId, UserId } from "@vote4dance/judging-types";

export type DanceTeamItem = DanceTeam
& {
    team_count: string,
    team_user_ids: string
    team_members: string,
    registration_count: number
}

export type DanceTeamUserItem = DanceTeam
& DanceTeamUser
& Pick<User, 'user_id' | 'user_firstname' | 'user_lastname' | 'user_email' | 'user_image' | 'user_birthdate' | 'user_country'>
& {
    organization_tag: string,
    organization_name: string,
    user_externals: string
}

type DanceTeamUserItemCompanion = {
    getFullName(user: DanceTeamUserItem): string,
    getExternalForFederation(user: DanceTeamUserItem, federation_id: FederationId): string
}

export const DanceTeamUserItem: DanceTeamUserItemCompanion = {
    getFullName(user: DanceTeamUserItem): string {
        return user.user_firstname + ' ' + user.user_lastname;
    },

    getExternalForFederation(user: DanceTeamUserItem, federation_id: FederationId): string {
        const src = (user as any)?.user_externals;
        if (!src) return "";

        const key: FederationId = federation_id;

        const toMap = (val: unknown): Map<FederationId, string> => {
            const m = new Map<FederationId, string>();

            if (typeof val === "string") {
            val
                .split(/[,;|]/)
                .map(s => s.trim())
                .filter(Boolean)
                .forEach(pair => {
                const [kRaw, vRaw] = pair.split(/[:=]/).map(s => s?.trim());
                if (kRaw && vRaw) m.set(kRaw as FederationId, vRaw);
                });
            return m;
            }

            if (Array.isArray(val)) {
            val.forEach(e => {
                const kSrc = (e as any)?.federation_id ?? (e as any)?.fed_id ?? (e as any)?.federation;
                const vSrc = (e as any)?.external_value ?? (e as any)?.value ?? (e as any)?.externalId;
                if (kSrc != null && vSrc != null) {
                m.set(String(kSrc) as FederationId, String(vSrc));
                }
            });
            return m;
            }

            if (typeof val === "object" && val) {
            Object.entries(val as Record<string, any>).forEach(([k, v]) => {
                if (k && v != null) m.set(k as FederationId, String(v));
            });
            }

            return m;
        };

        return toMap(src).get(key) ?? "";
    }
}

export function DanceTeamSource() {
    return new StreamSource<DanceTeamItem[]>('/danceteams');
}

export function DanceTeamItemSource(dance_team_id: DanceTeamId) {
    return new StreamSource<DanceTeamItem>(`/danceteams/${dance_team_id}`);
}

export function UserDanceTeamSource(user_id?: UserId) {
    return new StreamSource<DanceTeamItem[]>(`/userdanceteams/${user_id}`);
}

export function DanceTeamUsersSource(dance_team_id: DanceTeamId) {
    return new StreamSource<DanceTeamUserItem[]>(`/danceteams/${dance_team_id}/users`);
}

export function OrganizationTeamSource(organization_id: OrganizationId) {
    return new StreamSource<DanceTeamItem[]>(`/organizations/${organization_id}/teams`);
}
